import { useState } from 'react';
import { KeyValue, UnicodeEntity } from '@mews/enums';
import { Field, FieldProps } from '@optimus-web/core';
import { Chips } from './Chips';

const ChipInputField = Field(Chips);

export interface ChipInputProps extends FieldProps {
    delimiters: UnicodeEntity[];
    onChange: (e: React.ChangeEvent<HTMLInputElement> | React.SyntheticEvent, value: string[]) => void;
    value: string[];
    invalidChips?: string[];
}

export const ChipInput = ({
    delimiters,
    onChange,
    error,
    invalidChips,
    value: chips,
    onBlur,
    ...other
}: ChipInputProps) => {
    const [inputValue, setInputValue] = useState('');

    const delimitersPattern = new RegExp(delimiters.join('|'), 'g');

    const handleRemoveChip = (event: React.SyntheticEvent, value: string | number | boolean) => {
        const newTags = chips.filter((chip) => chip !== value);

        onChange(event, newTags);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === KeyValue.Backspace && !inputValue && chips.length > 0) {
            onChange(event, chips.slice(0, -1));
        }
    };

    const parseValueIntoTags = (value: string) => [
        ...new Set(
            value
                .split(delimitersPattern)
                .filter((newChip) => Boolean(newChip) && !chips.some((chip) => chip === newChip))
        ),
    ];

    const handleParseValue = (
        event: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
        value: string
    ) => {
        const newChips = parseValueIntoTags(value);

        if (newChips.length > 0) {
            const finalChips = [...chips, ...newChips];

            onChange(event, finalChips);
        }

        setInputValue('');
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        setInputValue(value);

        if (delimitersPattern.test(value)) {
            handleParseValue(event, value);
        }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        if (inputValue) {
            handleParseValue(event, inputValue);
        }

        onBlur?.(event);
    };

    return (
        <ChipInputField
            chips={chips}
            value={inputValue}
            handleRemoveChip={handleRemoveChip}
            handleKeyDown={handleKeyDown}
            handleChange={handleChange}
            onBlur={handleBlur}
            error={error}
            invalidChips={invalidChips}
            {...other}
        />
    );
};
